import { Injectable, NgZone } from "@angular/core";
import { MILLISECONDS_IN_SECOND } from "@dtm-frontend/shared/utils";
import { KeyboardEventModifier, ScreenSpaceEventHandler } from "cesium";
import { Connectable, connectable, throttleTime } from "rxjs";
import { CesiumMapService } from "../cesium-map.service";
import { DtmCesiumEvents, DtmCesiumLongPressEvents } from "./cesium-events.model";
import { DtmCesiumLongPressObserver } from "./cesium-long-press-observer";
import { DtmCesiumPureEventObserver } from "./cesium-pure-event-observer";

const MAX_EVENTS_PER_SECOND = 30;
const CESIUM_EVENTS_THROTTLE_TIME_MILLISECONDS = MILLISECONDS_IN_SECOND / MAX_EVENTS_PER_SECOND;

@Injectable()
export class ThrottledCesiumEventBuilder {
    public static longPressEvents: Set<DtmCesiumLongPressEvents> = new Set([
        DtmCesiumLongPressEvents.LONG_LEFT_PRESS,
        DtmCesiumLongPressEvents.LONG_RIGHT_PRESS,
        DtmCesiumLongPressEvents.LONG_MIDDLE_PRESS,
    ]);

    private eventsHandler: ScreenSpaceEventHandler | undefined;
    private cesiumEventsObservables = new Map<string, Connectable<ScreenSpaceEventHandler.MotionEvent>>();

    constructor(private readonly cesiumMapService: CesiumMapService, private readonly ngZone: NgZone) {}

    public static getEventFullName(event: DtmCesiumEvents, modifier?: KeyboardEventModifier): string {
        if (modifier) {
            return `${event}_${modifier}`;
        } else {
            return event.toString();
        }
    }

    public init() {
        // Initialize the cesium event handler outside Angular to avoid change detection on every event
        this.ngZone.runOutsideAngular(() => {
            this.eventsHandler = this.cesiumMapService.getViewer().screenSpaceEventHandler;
        });
    }

    public get(event: DtmCesiumEvents, modifier?: KeyboardEventModifier) {
        const eventName = ThrottledCesiumEventBuilder.getEventFullName(event, modifier);
        let eventObserver = this.cesiumEventsObservables.get(eventName);
        if (!eventObserver) {
            eventObserver = this.ngZone.runOutsideAngular(() => this.createCesiumEventObservable(event, modifier));
            this.cesiumEventsObservables.set(eventName, eventObserver);
        }

        return eventObserver.pipe(throttleTime(CESIUM_EVENTS_THROTTLE_TIME_MILLISECONDS));
    }

    private createCesiumEventObservable(
        event: DtmCesiumEvents,
        modifier?: KeyboardEventModifier
    ): Connectable<ScreenSpaceEventHandler.MotionEvent> {
        let cesiumEventObservable: Connectable<ScreenSpaceEventHandler.MotionEvent>;
        if (ThrottledCesiumEventBuilder.longPressEvents.has(event as DtmCesiumLongPressEvents)) {
            cesiumEventObservable = this.createSpecialCesiumEventObservable(event as DtmCesiumLongPressEvents, modifier);
        } else if (this.eventsHandler) {
            cesiumEventObservable = connectable(new DtmCesiumPureEventObserver(event, modifier).init(this.eventsHandler));
        } else {
            throw new Error("ThrottledCesiumEventBuilder not initialized. Call init() before using.");
        }
        this.ngZone.runOutsideAngular(() => cesiumEventObservable.connect());

        return cesiumEventObservable;
    }

    private createSpecialCesiumEventObservable(event: DtmCesiumLongPressEvents, modifier?: KeyboardEventModifier) {
        // could support more events if needed
        return new DtmCesiumLongPressObserver(event, modifier, this).init();
    }
}
